/**
 * Sessie 53 blok 3 (L3): Gezondheid per vraagtype, dertig dagen.
 *
 * Per vraagtype de stand van het antwoord (woord plus kleur, nooit alleen
 * kleur), de teller tot het volgende voorstel ("7 van 10", alleen als er
 * nog een voorstel kan komen), hoe vaak het concept ongewijzigd de deur
 * uit ging, de correcties (uit te klappen per soort, met "waarvan via
 * copiloot") en de gemiddelde kosten per mailconcept. Meeste verkeer bovenaan;
 * vraagtypen zonder verkeer staan ingeklapt onderaan. Een blok dat niet
 * laadt zegt dat zelf.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  vraagtypenGezondheidApi,
  type VraagtypeGezondheid,
  type VraagtypeStand,
} from "@/lib/api";

import { toontTeller, type Rijdata } from "./vraagtypeTeller";

const STAND_WOORD: Record<VraagtypeStand, string> = {
  rood: "Rood",
  oranje: "Oranje",
  akkoord: "Akkoord",
  groen: "Groen",
};

// Okabe-Ito-tokens van de werkplek; het woord staat er altijd bij.
const STAND_CHIP: Record<VraagtypeStand, string> = {
  rood: "wp-chip wp-chip-risico",
  oranje: "wp-chip wp-chip-wekker",
  akkoord: "wp-chip wp-chip-mail",
  groen: "wp-chip wp-chip-whatsapp",
};

// De copiloot staat er niet los tussen: die hangt aan een verzending en
// telt niet mee in de correcties (fix K2, punt 9).
const SOORT_WOORD: [keyof VraagtypeGezondheid["per_soort"], string][] = [
  ["verzending", "aangepast verstuurd"],
  ["afwijzing", "afgewezen"],
  ["afgerond_zonder_antwoord", "zonder antwoord"],
];

function euro(waarde: number): string {
  return waarde.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}

function StandChip({ stand }: { stand: VraagtypeStand | null }) {
  if (!stand) return <span className="wp-chip wp-chip-grijs">geen stand</span>;
  return (
    <span className={STAND_CHIP[stand]} data-stand={stand}>
      {STAND_WOORD[stand]}
    </span>
  );
}

function Rij({ rij }: { rij: Rijdata }) {
  const [open, zetOpen] = useState(false);
  const sleutel = rij.vraagtype ?? "zonder";
  const viaCopiloot = rij.via_copiloot ?? rij.per_soort.copiloot;
  return (
    <li className="wp-gz-vt-rij" data-testid={`vraagtype-${sleutel}`}>
      <div className="wp-gz-vt-kop">
        <span className="wp-gz-vt-naam">{rij.naam}</span>
        <StandChip stand={rij.stand} />
      </div>
      <div className="wp-gz-vt-cijfers">
        <span className="wp-gz-vt-cel">
          {toontTeller(rij) ? `${rij.teller} van ${rij.n}` : ""}
        </span>
        <span className="wp-gz-vt-cel">{rij.ongewijzigd} ongewijzigd</span>
        <span className="wp-gz-vt-cel">
          {rij.correcties > 0 ? (
            <button
              type="button"
              className="wp-gz-vt-knop"
              aria-expanded={open}
              aria-controls={`vt-soorten-${sleutel}`}
              onClick={() => zetOpen((o) => !o)}
            >
              {rij.correcties} {rij.correcties === 1 ? "correctie" : "correcties"}
              <span aria-hidden="true" className="wp-gz-vt-pijl">
                {open ? "▾" : "▸"}
              </span>
            </button>
          ) : (
            "0 correcties"
          )}
        </span>
        <span className="wp-gz-vt-cel wp-gz-vt-kosten">
          {rij.gemiddeld_eur !== null ? (
            <>
              <span className="wp-gz-mk-bedrag">{euro(rij.gemiddeld_eur)}</span> per concept
            </>
          ) : (
            ""
          )}
        </span>
      </div>
      {open ? (
        <p className="wp-gz-vt-soorten" id={`vt-soorten-${sleutel}`}>
          {SOORT_WOORD.map(([soort, woord]) => (
            <span key={soort}>
              <b>{rij.per_soort[soort]}</b> {woord}
            </span>
          ))}
          {viaCopiloot > 0 ? (
            <span>
              waarvan <b>{viaCopiloot}</b> via copiloot
            </span>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}

export function PerVraagtype() {
  const vraag = useQuery({
    queryKey: ["werkplek", "gezondheid", "vraagtypen"],
    queryFn: vraagtypenGezondheidApi.haal,
  });
  const [stilOpen, zetStilOpen] = useState(false);
  const data = vraag.data;
  const metVerkeer = data?.vraagtypen.filter((v) => v.verkeer > 0) ?? [];
  const zonderVerkeer = data?.vraagtypen.filter((v) => v.verkeer === 0) ?? [];

  return (
    <div className="wp-blok wp-gz-vt" data-testid="per-vraagtype">
      <h4>
        <span>Per vraagtype</span>
        <span className="wp-gz-mk-onder">{data?.dagen ?? 30} dagen</span>
      </h4>
      {vraag.isLoading ? (
        <p className="wp-gz-uitleg">Bezig met laden…</p>
      ) : vraag.isError || !data ? (
        <p className="wp-gz-uitleg" role="alert" style={{ color: "var(--wp-rood)" }}>
          De cijfers per vraagtype zijn niet op te halen.
        </p>
      ) : data.vraagtypen.length === 0 ? (
        <p className="wp-gz-uitleg" role="status">
          Nog geen vraagtypen.
        </p>
      ) : (
        <>
          {metVerkeer.length === 0 ? (
            <p className="wp-gz-uitleg" role="status">
              Geen verkeer in deze {data.dagen} dagen.
            </p>
          ) : (
            <ul className="wp-gz-vt-lijst">
              {metVerkeer.map((rij) => (
                <Rij key={rij.vraagtype ?? "zonder"} rij={rij} />
              ))}
            </ul>
          )}
          {zonderVerkeer.length > 0 ? (
            <>
              <button
                type="button"
                className="wp-gz-vt-knop wp-gz-vt-stil"
                aria-expanded={stilOpen}
                onClick={() => zetStilOpen((o) => !o)}
              >
                Zonder verkeer ({zonderVerkeer.length})
                <span aria-hidden="true" className="wp-gz-vt-pijl">
                  {stilOpen ? "▾" : "▸"}
                </span>
              </button>
              {stilOpen ? (
                <ul className="wp-gz-vt-lijst" data-testid="zonder-verkeer">
                  {zonderVerkeer.map((rij) => (
                    <Rij key={rij.vraagtype ?? "zonder"} rij={rij} />
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
